import { BusinessObjectBase } from './base';
import { packageEntitlementSchema } from '@schemas/business-objects/package-entitlement';
import type { User } from './user';
import type { EntitlementKind, Id } from '../base-types';

export interface PackageEntitlementData {
  readonly id: Id;
  readonly kind?: EntitlementKind;
  readonly packageNamePattern?: string;
  readonly expiresAt?: Date;
  readonly source?: string;
  readonly userId: Id;
  readonly user?: User;
}

export interface PackageEntitlement extends Readonly<PackageEntitlementData> {}
type PackageEntitlementCreateWithChildrenData = Partial<PackageEntitlementData>;

export class PackageEntitlement extends BusinessObjectBase {
  static readonly entityName = 'PackageEntitlement' as const;
  static readonly plural = 'package-entitlements';
  static schema = packageEntitlementSchema;

  // --- Static CRUD methods ---

  static async find(_filter?: any): Promise<PackageEntitlement[]> {
    throw new Error('find is not available on PackageEntitlement');
  }

  static async findOne(_filter?: any): Promise<PackageEntitlement | null> {
    throw new Error('findOne is not available on PackageEntitlement');
  }

  static async findById(_id: Id, _filter?: any): Promise<PackageEntitlement> {
    throw new Error('findById is not available on PackageEntitlement');
  }

  static async create(
    data: Omit<PackageEntitlementData, 'id'>,
  ): Promise<PackageEntitlement> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new PackageEntitlement(result);
  }

  static async createMany(
    data: Omit<PackageEntitlementData, 'id'>[],
  ): Promise<PackageEntitlement[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new PackageEntitlement(item));
  }

  static async createWithChildren(
    data: PackageEntitlementCreateWithChildrenData,
  ): Promise<PackageEntitlement> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new PackageEntitlement(result);
  }

  static async createManyWithChildren(
    data: PackageEntitlementCreateWithChildrenData[],
  ): Promise<PackageEntitlement[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new PackageEntitlement(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<PackageEntitlementData>,
  ): Promise<PackageEntitlement> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new PackageEntitlement(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
